import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly } from '../../../../../utils/dateUtils';
import { RowState, resolveRowStateText, rowStateSeverity, parseChangedFields, resolveFieldLabel } from '../../../../../utils/dealerApprovalEnums';
import '../../../dealerApproval/dealerApproval.scss';

const GenericTable = ({
    value = [],
    lazyParams = {},
    currentFirst = 0,
    handlePage,
    onSort,
    sortField,
    sortOrder,
    totalRecords = 0,
    showGridlines = true,
    loading = false,
    filters = {},
    globalFilterFields = [],
    emptyMessage = 'Kayıt bulunamadı.',
    rowsPerPageOptions = [5, 10, 25, 50, 100, 500, 1000],
    columns = [],
    onCreate,
    onEdit,
    onHistory,
    // Onay akışı bağlamı: approvalContext true iken "Durum" kolonu gösterilir.
    // Ayrıca satırlarda rowState > 0 varsa da durum kolonu görünür (eski ekran staging).
    approvalContext = false,
    readOnly = false,
    // Bölge Yöneticisi gibi sekmelerde otomatik kapatma yok; rowState=5 gelse bile canlı gibi göster.
    suppressCloseOut = false
}) => {
    const { th } = useLanguage();

    const displayRowState = (rowData) => {
        const rowState = rowData?.rowState ?? RowState.LIVE;
        if (suppressCloseOut && rowState === RowState.PENDING_CLOSE_OUT) return RowState.LIVE;
        return rowState;
    };

    const showStatusColumn = approvalContext || (value || []).some((r) => displayRowState(r) > 0);

    const actionBodyTemplate = (rowData) => {
        const rowState = displayRowState(rowData);
        // Eski (değiştirilecek), silinecek veya kapatılacak satırda düzenleme ikonu gösterme.
        const canEditRow = !readOnly && rowState !== RowState.PENDING_UPDATE_OLD && rowState !== RowState.PENDING_DELETE && rowState !== RowState.PENDING_CLOSE_OUT;
        return (
            <div className="flex gap-2 justify-content-center">
                {canEditRow && <i onClick={() => onEdit && onEdit(rowData)} className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('common.edit')}></i>}
                {onHistory && <i onClick={() => onHistory(rowData)} className="pi pi-history cursor-pointer text-blue-500 hover:text-blue-700" style={{ fontSize: '1.1rem' }} title={th('warrantyClaims.tooltips.history')} />}
            </div>
        );
    };

    const buildChangedTooltip = (rowData) => {
        const changed = parseChangedFields(rowData.changedFieldsJson);
        if (!changed || typeof changed !== 'object') return undefined;
        return Object.keys(changed)
            .map((key) => {
                const entry = changed[key];
                const label = resolveFieldLabel(th, key);
                if (entry && typeof entry === 'object' && ('old' in entry || 'new' in entry)) {
                    return `${label}: ${entry.old ?? '-'} → ${entry.new ?? '-'}`;
                }
                return `${label}: ${entry ?? '-'}`;
            })
            .join('\n');
    };

    const statusBodyTemplate = (rowData) => {
        const rowState = displayRowState(rowData);
        const tooltip = buildChangedTooltip(rowData);
        return <Tag value={resolveRowStateText(th, rowState)} severity={rowStateSeverity(rowState)} icon={rowState === RowState.LIVE ? 'pi pi-check' : 'pi pi-clock'} title={tooltip} />;
    };

    const rowClassName = (rowData) => {
        const rowState = displayRowState(rowData);
        if (rowState === RowState.LIVE) return '';
        return `dealer-row-state-${rowState}`;
    };

    const closeOutPreview = (rowData, field) => {
        if (displayRowState(rowData) !== RowState.PENDING_CLOSE_OUT) return null;
        const entry = parseChangedFields(rowData.changedFieldsJson)?.[field];
        const next = entry && typeof entry === 'object' ? entry.new : entry;
        if (!next) return null;
        const text = formatDateOnly(next) || String(next);
        return (
            <span className="dealer-close-out-preview" title={`${th('dealerApproval.rowState.5')}: ${text}`}>
                {` \u2192 ${text}`}
            </span>
        );
    };

    const resolveColumnBody = (col) => {
        const baseBody = col.body
            ? col.body
            : col.bodyField
            ? (rowData) => {
                  const val = rowData[col.bodyField];
                  return val ? formatDateOnly(val) : '';
              }
            : undefined;

        if (!/endDate$/i.test(col.field || '')) {
            return baseBody;
        }

        const EndDateColumnBody = (rowData, ...rest) => {
            const content = baseBody ? baseBody(rowData, ...rest) : rowData?.[col.field];
            const preview = closeOutPreview(rowData, col.field);
            if (!preview) return content;
            return (
                <>
                    {content}
                    {preview}
                </>
            );
        };
        return EndDateColumnBody;
    };

    return (
        <>
            {onCreate && !readOnly && (
                <div style={{ display: 'flex', marginLeft: 'auto' }}>
                    <Button label={th('common.new')} icon="pi pi-plus" onClick={onCreate} className="mb-2 d-flex ml-auto" />
                </div>
            )}

            <DataTable
                value={
                    value?.map((item, index) => ({
                        ...item,
                        _customKey: `${item.id || item.Id || index}_${index}`
                    })) || []
                }
                dataKey="_customKey"
                rowClassName={showStatusColumn ? rowClassName : undefined}
                lazy
                paginator
                first={currentFirst}
                rows={lazyParams.rows || 10}
                onPage={handlePage}
                onSort={onSort}
                sortField={sortField}
                sortOrder={sortOrder}
                totalRecords={totalRecords}
                showGridlines={showGridlines}
                loading={loading}
                filters={filters}
                filterDisplay="menu"
                globalFilterFields={globalFilterFields}
                emptyMessage={th('warrantyClaims.detail.labor.emptyMessage')}
                rowsPerPageOptions={rowsPerPageOptions}
                tableStyle={{ minWidth: '50rem' }}
                stripedRows
                removableSort
                sortMode="single"
            >
                <Column header="" bodyClassName="text-center" headerClassName="text-center" body={actionBodyTemplate} exportable={false} style={{ width: '5%', minWidth: '33px' }} />
                {showStatusColumn && <Column header={th('dealerApproval.statusColumn')} body={statusBodyTemplate} exportable={false} style={{ width: '8%', minWidth: '120px' }} />}

                {columns.map((col) => (
                    <Column key={col.field} field={col.field} sortField={col.sortField} header={col.header} sortable={col.sortable !== false} style={col.style || {}} body={resolveColumnBody(col)} />
                ))}
            </DataTable>
        </>
    );
};

export default GenericTable;
